import { useCallback, useEffect, useState } from 'react';
import { isValidPuzzleId, todayPuzzleId } from '../utils/puzzleId';

/**
 * The archive's routing, as one optional query parameter.
 *
 * No router: this is a single screen with a single param, and react-router is
 * ~10KB plus a provider refactor for it. What that trade does require is
 * handling history manually — in particular the `popstate` listener below,
 * without which the browser Back button would change the URL and the app would
 * carry on showing the old puzzle.
 */

// The last change to the puzzle generator. Earlier dates cannot be reproduced
// faithfully (the placer changed ten times before this and the word list grew
// by ~2,000 entries), so they are deliberately not in the archive at all
// rather than served as plausible-looking reconstructions next to real
// historical leaderboard times.
export const ARCHIVE_START = 20260527;

/**
 * Whether to advertise the archive in the UI.
 *
 * Off while the product question — how past puzzles should actually be
 * surfaced and what role they play — is still open. Everything behind it works:
 * `?d=YYYYMMDD` loads any archived date, ArchiveBrowser renders and navigates,
 * and archive completions are recorded and correctly kept out of the rankings.
 * The only thing switched off is discovery.
 *
 * Flip to true to show the "Past puzzles" link again.
 */
export const ARCHIVE_LINK_VISIBLE = false;

const readParam = () => {
  if (typeof window === 'undefined') return null;
  const raw = new URLSearchParams(window.location.search).get('d');
  if (!raw) return null;

  const id = Number(raw);
  if (!isValidPuzzleId(raw)) return null;
  // Not before the archive begins, and never in the future.
  if (id < ARCHIVE_START || id > todayPuzzleId()) return null;
  return id;
};

const usePuzzleDateParam = () => {
  const [puzzleId, setPuzzleIdState] = useState(readParam);

  useEffect(() => {
    // Back/forward must re-read the URL; nothing else re-renders on history
    // navigation.
    const onPopState = () => setPuzzleIdState(readParam());
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  const setPuzzleId = useCallback((next) => {
    const isToday = next == null || String(next) === String(todayPuzzleId());
    const url = isToday ? window.location.pathname : `?d=${next}`;
    window.history.pushState({}, '', url);
    setPuzzleIdState(isToday ? null : Number(next));
  }, []);

  return {
    /** null means today. */
    puzzleId,
    isArchive: puzzleId != null,
    setPuzzleId,
  };
};

export default usePuzzleDateParam;
